import React, { useState } from "react";
import ConfirmModal from "../blog/ConfirmModal";
import ClustersTable from "./ClustersTable";
import KeywordDiffTable from "./KeywordDiffTable";
import StatCard from "./StatCard";
import { formatNumber } from "./clusterFormat";
import { useKeywordClusters } from "./useKeywordClusters";
import { useKeywordRefresh } from "./useKeywordRefresh";
import {
  KEYWORD_REFRESH_SCHEDULE,
  formatPacific,
  formatRelative,
  nextKeywordRefreshDate,
} from "../../../utils/schedule";

// The scheduled job no-ops outside prod (devFlag in scheduledKeywords.js), so
// the dev admin must not promise a run that will never happen.
const isDev = window.location.hostname.includes("dev.") || window.location.hostname === "localhost";

const KeywordClustersPanel = () => {
  const {
    lastRun, historyLoading, historyError,
    running, runKind, result, error,
    preview, refresh, clearResult,
  } = useKeywordRefresh();

  // Live view of the collection itself — repaints automatically when a refresh
  // commits, so the table below always reflects what generation will read.
  const {
    clusters,
    loading: clustersLoading,
    error: clustersError,
    totals,
  } = useKeywordClusters();

  const [confirmOpen, setConfirmOpen] = useState(false);

  const nextRun = nextKeywordRefreshDate();

  const doRefresh = () => {
    setConfirmOpen(false);
    refresh();
  };

  return (
    <div className="relative">
      {running && (
        <div className="absolute inset-0 z-50 flex flex-col items-center justify-start pt-6 bg-white/60 backdrop-blur-sm rounded-lg">
          <div className="text-center px-6">
            <div className="w-14 h-14 border-4 border-amber-200 border-t-amber-500 rounded-full animate-spin mx-auto mb-5" />
            <p className="text-gray-700 text-base font-semibold mb-2">
              {runKind === "preview" ? "🔍 Previewing keyword metrics…" : "🔄 Refreshing keyword clusters…"}
            </p>
            <p className="text-gray-500 text-sm">
              Querying DataForSEO — this can take a minute. Please don't close this page.
            </p>
          </div>
        </div>
      )}

      {confirmOpen && (
        <ConfirmModal
          title="Refresh Keyword Clusters"
          message="This calls DataForSEO (~$0.14) and overwrites the stored volume, difficulty and ranking figures for every cluster. Human-authored strategy fields are preserved."
          confirmLabel="🔄 Refresh"
          confirmClass="bg-amber-600 hover:bg-amber-700"
          onConfirm={doRefresh}
          onCancel={() => setConfirmOpen(false)}
        />
      )}

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-xl font-bold text-gray-800">SEO — Keyword Clusters</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Search volume, difficulty and toonkids.com rankings from DataForSEO.
            Refreshes automatically on the {KEYWORD_REFRESH_SCHEDULE.label}.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={preview}
            disabled={running}
            className="px-4 py-2 bg-sky-100 text-sky-700 text-sm font-medium rounded-lg hover:bg-sky-200 disabled:opacity-50 transition-colors"
            title="Fetch live metrics and show the diff without saving anything"
          >
            🔍 Preview (dry run)
          </button>
          <button
            onClick={() => setConfirmOpen(true)}
            disabled={running}
            className="px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 disabled:opacity-50 transition-colors"
            title="Fetch live metrics and save them to Firestore"
          >
            🔄 Refresh Keywords
          </button>
        </div>
      </div>

      {/* ── Status cards ──────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <StatCard label="Current inventory">
          {clustersLoading && <p className="text-gray-400 text-sm">Loading…</p>}
          {!clustersLoading && clustersError && (
            <p className="text-red-600 text-sm">{clustersError}</p>
          )}
          {!clustersLoading && !clustersError && (
            <>
              <p className="text-lg font-semibold text-gray-900">
                {totals.clusters} clusters · {totals.keywords} keywords
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {formatNumber(totals.volume)} combined monthly searches
              </p>
              <p className="text-xs text-gray-600 mt-2">
                {totals.ranked} keyword{totals.ranked === 1 ? "" : "s"} currently ranking
              </p>
            </>
          )}
        </StatCard>

        <StatCard label="Last refresh">
          {historyLoading && <p className="text-gray-400 text-sm">Loading…</p>}
          {!historyLoading && historyError && (
            <p className="text-red-600 text-sm">{historyError}</p>
          )}
          {!historyLoading && !historyError && !lastRun && (
            <>
              <p className="text-lg font-semibold text-gray-900">Never refreshed</p>
              <p className="text-xs text-gray-500 mt-1">
                Clusters are seeded but no refresh has run yet.
              </p>
            </>
          )}
          {!historyLoading && !historyError && lastRun && (
            <>
              <p className="text-lg font-semibold text-gray-900">
                {lastRun.runAt ? formatRelative(lastRun.runAt) : lastRun.id}
              </p>
              {lastRun.runAt && (
                <p className="text-xs text-gray-500 mt-1">{formatPacific(lastRun.runAt)}</p>
              )}
              <p className="text-xs text-gray-600 mt-2">
                {lastRun.clustersUpdated ?? "—"} clusters
                {typeof lastRun.cost === "number" && <> · ${lastRun.cost}</>}
                {lastRun.balanceAfter !== null && lastRun.balanceAfter !== undefined && (
                  <> · balance ${lastRun.balanceAfter}</>
                )}
              </p>
            </>
          )}
        </StatCard>

        <StatCard label="Next scheduled refresh">
          {isDev ? (
            <>
              <p className="text-lg font-semibold text-gray-900">Not scheduled in dev</p>
              <p className="text-xs text-gray-500 mt-1">
                The monthly job only runs in production. Use the buttons above to trigger it manually.
              </p>
            </>
          ) : (
            <>
              <p className="text-lg font-semibold text-gray-900">{formatRelative(nextRun)}</p>
              <p className="text-xs text-gray-500 mt-1">{formatPacific(nextRun)}</p>
              <p className="text-xs text-gray-600 mt-2">{KEYWORD_REFRESH_SCHEDULE.label}</p>
            </>
          )}
        </StatCard>
      </div>

      {/* ── Error ─────────────────────────────────────────────────────────── */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 text-sm">
          <strong>Refresh failed:</strong> {error}
        </div>
      )}

      {/* ── Result ────────────────────────────────────────────────────────── */}
      {result && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
            <div>
              <h3 className="font-bold text-gray-800">
                {result.dryRun ? "🔍 Preview result (nothing saved)" : "✅ Refresh complete"}
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {result.clustersUpdated} clusters ·{" "}
                {result.keywordsReceived}/{result.keywordsRequested} keywords ·{" "}
                {result.rankedKeywords} ranked for {result.target} · ${result.cost}
                {result.balanceAfter !== null && result.balanceAfter !== undefined && (
                  <> · balance ${result.balanceAfter}</>
                )}
              </p>
            </div>
            <button
              onClick={clearResult}
              className="text-sm text-gray-500 hover:text-gray-800 shrink-0"
            >
              ✕ Dismiss
            </button>
          </div>
          <KeywordDiffTable diff={result.diff} />
        </div>
      )}

      {/* ── Live clusters ─────────────────────────────────────────────────── */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-5 py-4 border-b border-gray-200">
          <h3 className="font-bold text-gray-800">Clusters &amp; keywords</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Live from <code className="font-mono">blog_keyword_clusters</code>, in the same
            priority order blog generation reads them. Click a cluster to see its keywords.
          </p>
        </div>
        <ClustersTable
          clusters={clusters}
          loading={clustersLoading}
          error={clustersError}
        />
      </div>
    </div>
  );
};

export default KeywordClustersPanel;
